/**
 * Drawer - shared slide-out panel primitive (Story #5351 W1-T3).
 *
 * Extracted from the shell duplicated in RecordDetailPanel.tsx and
 * ModelPickerDrawer.tsx (overlay, close-on-outside-click, close-on-Escape,
 * slide-in animation). Adds a focus trap + aria-modal so both consumers
 * gain accessibility for free; callers own the header/body content.
 */
import React, { useEffect, useRef } from 'react';

interface DrawerProps {
  onClose: () => void;
  children: React.ReactNode;
  /** Accessible name for the dialog region — required, screen readers need one. */
  ariaLabel: string;
  /** Tailwind max-width class for the panel. Default matches RecordDetailPanel's. */
  maxWidthClassName?: string;
  /** Extra classes merged onto the panel element (background, etc). */
  panelClassName?: string;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Drawer: React.FC<DrawerProps> = ({
  onClose, children, ariaLabel, maxWidthClassName = 'max-w-lg', panelClassName = 'bg-gray-100',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Close on outside click. Delayed so the click that opened the drawer doesn't also close it.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 100);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleClick); };
  }, [onClose]);

  // Close on Escape.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Focus trap: move focus into the panel on mount, cycle Tab/Shift+Tab within it,
  // restore focus to whatever held it before the drawer opened.
  useEffect(() => {
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const firstFocusable = panel?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? panel)?.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      previouslyFocused.current?.focus();
    };
  }, []);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={`fixed top-0 right-0 h-full w-full ${maxWidthClassName} ${panelClassName} shadow-2xl z-50 flex flex-col w1-drawer-slide-in`}
      >
        {children}
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } } .w1-drawer-slide-in { animation: slideInRight 0.2s ease-out; }`}</style>
    </>
  );
};

export default Drawer;
